// 直接引具体目录而非 components barrel：barrel 会把全部组件（含
// AgeVerification / AgreementConfirmation 及其 SCSS）拉进 App 的静态闭包，
// Vite 随之把这些 CSS 提升到首屏 MainShell.css。这里只需要 AppRouter。
import AppRouter from "./components/AppRouter";
import "./index.css";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { initScreenWidth } from "./utils/screenWidth";
import { useFavoriteGames } from "./hooks/useFavoriteGames";
import { useIOSKeyboardFix } from "./hooks/useIOSKeyboardFix";
import { useUserDataFlattened } from "@/modules/user/stores";
// PopupQueueProvider 走深层路径：contexts barrel 会一并带出 FloatingIconsContext，
// 而后者静态 import useFloatingIcons（12 个活动弹窗 hook，约 160KB），会抵消下面的 lazy。
import { PopupQueueProvider } from "./contexts/PopupQueueContext";
// 直接具体路径（不走 components/Event barrel，那条 barrel 刻意只导出 GlobalEvent 以免整棵
// 弹窗树进首屏 chunk）。本组件只依赖 FloatingIconsContext，而它已被上一行静态引入，不新增图。
import { ActivityPopupBridge } from "./components/Event/ActivityPopupBridge";
import { useCustomerServices } from "./modules/homeDisplay/hooks";

import { useGlobalValue } from "./stores/GlobalState";
import { SilentErrorBoundary } from "./components/ChunkLoadBoundary/ChunkLoadBoundary";
import "./utils/imageReloadManager";
// 合规拦截页：设备黑名单时必须可靠渲染，保持静态导入（懒加载无收益且会引入 chunk 加载失败面）
import BlockedDevicePage from "./pages/BlockedDevice";
// 只取常量，从具体文件 deep import——避免走 ./components/Event barrel（会静态带出
// GlobalEvent 整棵弹窗树，把 ~500KB 拉进首屏关键路径，架空下方的 lazy(GlobalEvent)）
import { APP_GLOBAL_EVENT_FLOATING_VISIBILITY } from "./components/Event/cornerFloatingIconsVisibility";
import { useChatMountRequested } from "./components/chatMount";
import { useStartupData } from "./modules/startup/stores";
import { resolvePrerequisiteFlag } from "./config/popupConfig";

// LCP/LT 优化：把非首屏关键的弹窗 / Push 等组件延后挂载。
//
// 旧实现只用 requestIdleCallback（3s 兜底），但 idle 判定的是"主线程空闲"——首屏
// 2~4s 恰恰因为在等网络而空闲，闸门于是在骨架屏还没填上时就打开，把一批弹窗接口
// 和客服 SDK 塞进了首屏关键窗口（实测 idle 在 2852ms 触发，游戏区 4177ms 才好）。
//
// 现在改为：先等 load（首屏资源基本落地）→ 再等一次 idle → 且带硬超时兜底，
// 保证最迟一定放行。客服 SDK 则完全移出本闸门，改为点客服才挂载（见 components/chatMount）。
type IdleRequestCallback = (deadline: { didTimeout: boolean }) => void;
type RequestIdleCallback = (
  cb: IdleRequestCallback,
  opts?: { timeout: number }
) => number;
type CancelIdleCallback = (handle: number) => void;

function useDeferredMount(hardTimeoutMs = 8000): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return;

    let done = false;
    let idleHandle: number | undefined;
    let softTimeoutId: ReturnType<typeof setTimeout> | undefined;

    const trigger = () => {
      if (done) return;
      done = true;
      setReady(true);
    };

    const ric = (
      window as unknown as { requestIdleCallback?: RequestIdleCallback }
    ).requestIdleCallback;
    const cic = (
      window as unknown as { cancelIdleCallback?: CancelIdleCallback }
    ).cancelIdleCallback;

    // 硬兜底：无论 load / idle 是否到达，最迟 hardTimeoutMs 一定放行。
    // 这条是"弹窗永远不弹"的保险，不能去掉。
    const hardTimeoutId = setTimeout(trigger, hardTimeoutMs);

    const afterLoad = () => {
      if (done) return;
      // load 之后再让一次 idle，避开收尾的图片解码；仍有 hardTimeoutId 兜底
      if (typeof ric === "function") {
        idleHandle = ric(trigger, { timeout: 1500 });
      } else {
        softTimeoutId = setTimeout(trigger, 300);
      }
    };

    if (document.readyState === "complete") {
      afterLoad();
    } else {
      window.addEventListener("load", afterLoad, { once: true });
    }

    return () => {
      done = true;
      clearTimeout(hardTimeoutId);
      window.removeEventListener("load", afterLoad);
      if (idleHandle !== undefined && typeof cic === "function") {
        cic(idleHandle);
      }
      if (softTimeoutId !== undefined) {
        clearTimeout(softTimeoutId);
      }
    };
  }, [ready, hardTimeoutMs]);

  return ready;
}

// 登录注册面板：整套表单 + 4 个附属对话框（找回密码 / 邮箱重置 / 重置密码 /
// 尝试过多）约 87KB 源码，全都压在 MainShell 里。它只在用户点登录注册、或访问
// /login·/register（那两条路由是重定向器，弹窗打开后即跳回首页）时才需要。
//
// 之所以现在能 lazy：triggerLoginRegisterPopup 里的 listenerReady 闸门会在监听器
// 就绪前把 open 请求缓存下来（且无 TTL），组件挂载时 consumePendingLoginRegisterPopup
// 取走补弹 —— 事件不会像裸 CustomEvent 那样丢掉。
// 但必须给它【独立的 Suspense 边界】：与下面那批 lazy 弹窗同边界的话，监听器要等
// 那一整批 chunk 都到齐才挂载，自动弹出会被拖很久。
const LoginRegisterPopup = lazy(() =>
  import("./components/LoginRegisterPopup").then(m => ({
    default: m.LoginRegisterPopup,
  }))
);
const LeftBottomFloatingIcons = lazy(() =>
  import("./components/LeftBottomFloatingIcons").then(m => ({
    default: m.LeftBottomFloatingIcons,
  }))
);
const TawkToChat = lazy(() =>
  import("./components/TawkToChat").then(m => ({ default: m.TawkToChat }))
);
const LiveChat = lazy(() =>
  import("./components/LiveChat").then(m => ({ default: m.LiveChat }))
);
const ChatwootChat = lazy(() =>
  import("./components/ChatwootChat").then(m => ({ default: m.ChatwootChat }))
);
const GlobalSafariAddHomeScreen = lazy(() =>
  import("./pages/LandingPage/components/SafariAddHomeScreen").then(m => ({
    default: m.GlobalSafariAddHomeScreen,
  }))
);
const GlobalDownloadAppPopup = lazy(() =>
  import("./components/DownloadAppPopup/GlobalDownloadAppPopup").then(m => ({
    default: m.GlobalDownloadAppPopup,
  }))
);
const GlobalAppDownloadDialog = lazy(() =>
  import("./components/AppDownloadDialog").then(m => ({
    default: m.GlobalAppDownloadDialog,
  }))
);
const GlobalApkUpgradeDialog = lazy(() =>
  import("./components/ApkUpgradeDialog/GlobalApkUpgradeDialog").then(m => ({
    default: m.GlobalApkUpgradeDialog,
  }))
);
const WebPush = lazy(() => import("./components/WebPush"));
const GlobalEvent = lazy(() =>
  import("./components/Event").then(m => ({ default: m.GlobalEvent }))
);
const GlobalRulesDialog = lazy(() =>
  import("./components/RulesDialog").then(m => ({
    default: m.GlobalRulesDialog,
  }))
);
// AgreementConfirmation 改 lazy：静态 import 会把它和 ProtocolDialog 的 SCSS
// 拉进 App 的首屏闭包。它是事件驱动的（监听 show-agreement-confirmation），
// 由 triggerAgreementConfirmation 的「迟到认领」保证 chunk 加载期间的事件不丢。
const AgreementConfirmation = lazy(() =>
  import("./components/AgreementConfirmation/AgreementConfirmation").then(
    m => ({
      default: m.AgreementConfirmation,
    })
  )
);
// InsurancePopup 改 lazy：静态 import 会把它 520KB JS + 257KB CSS（解压后）
// 整个拖进 App 的首屏静态闭包，而它只是首页 banner 点击才打开的事件驱动弹窗。
// 实测这一项占 preset 首屏关键载荷的 34%，4x CPU 降频下直接体现在骨架停留时长上。
//
// 仍然【立即挂载】、不放进 deferredReady：它是事件驱动的，闸门最长 8s，
// 这段时间内点 banner 会没反应。chunk 加载期间派发的事件由
// triggerInsurancePopup 的「迟到认领」补上。
const InsurancePopup = lazy(() =>
  import("./components/InsurancePopup/InsurancePopup").then(m => ({
    default: m.InsurancePopup,
  }))
);
// DepositPopup / WithdrawPopup 改 lazy：两者被 Vite 打进【同一个】chunk
// （519KB JS + 253KB CSS，解压后），静态 import 会整块进 App 的首屏闭包。
// 必须两个一起 lazy —— 只改一个，另一个仍会把这块共享 chunk 拉回首屏。
//
// 同样【立即挂载】、不进 deferredReady：都是事件驱动弹窗，闸门最长 8s，
// 这段时间内点充值/提现入口会没反应。chunk 加载期间派发的事件由各自触发器的
// 「迟到认领」补上。
const DepositPopup = lazy(() =>
  import("./components/DepositPopup").then(m => ({
    default: m.DepositPopup,
  }))
);
const WithdrawPopup = lazy(() =>
  import("./components/WithdrawPopup").then(m => ({
    default: m.WithdrawPopup,
  }))
);
// UnpaidGuideController 改 lazy（与源分支一致）：它静态 import 会把充值/未付订单
// 那一整块（本次实测 495KB JS + 235KB CSS 的共享 chunk）拖进 App 的首屏静态闭包。
// 只把渲染放进 deferredReady 是不够的——静态闭包不会因为延迟渲染而变小。
const UnpaidGuideController = lazy(() =>
  import("./components/UnpaidGuideModal").then(m => ({
    default: m.UnpaidGuideController,
  }))
);
// FloatingIconsProvider 改 lazy：它静态 import useFloatingIcons，后者又静态 import
// 12 个活动弹窗 hook（useFirstRechargePopup / useCashbackPopup / ...），共约 160KB
// 落在首屏静态闭包里。
//
// 之所以延后安全：浮标列表来自 /api/home_display/config
// （useLeftBottomFloatingIcon → useDisplayItemsByPosition → useDisplayItems），
// config 没回来之前 LeftBottomFloatingIcons 的 iconsToRender 为空、一个图标都不渲染
// （见其第 110 行的空列表分支）。实测 config 在 4G 下要 140~1387ms 才落地，
// 而这 160KB 的 JS 完全可以和它并行加载 —— 放在首屏静态闭包里买不到任何东西。
// 同理，活动弹窗只能由浮标点击 / 首页活动入口打开，两者都要等 config，不存在
// 「chunk 还没到就有人要开弹窗」的窗口。
const FloatingIconsProvider = lazy(() =>
  import("./contexts/FloatingIconsProvider").then(m => ({
    default: m.FloatingIconsProvider,
  }))
);
// navigation 轴的额外导航件（supergol=左抽屉）；template1 渲染 null，对应 chunk 也只在选中 variant 时生成
const NavigationExtras = lazy(() =>
  import("./navigation").then(m => ({ default: m.NavigationExtras }))
);
const PrerequisitePopups = lazy(() =>
  import("./components/PrerequisitePopups/PrerequisitePopups").then(m => ({
    default: m.PrerequisitePopups,
  }))
);
function App() {
  useFavoriteGames();
  useIOSKeyboardFix();

  const [deviceBlacklisted] = useGlobalValue("deviceBlacklisted");
  const { isAuthenticated, userId, username, email, vipLevel } =
    useUserDataFlattened();
  // Chatwoot 联系人名称：用户名后拼接 VIP 等级，如 xxx-VIP1；无等级时只用用户名
  const chatwootUserName =
    username != null && vipLevel?.level != null
      ? `${username}-VIP${vipLevel.level}`
      : username;
  const customerServices = useCustomerServices();
  // 非关键弹窗：等 load + idle，8s 硬兜底
  const deferredReady = useDeferredMount();
  // 客服 SDK：只有用户真正点了客服才挂载
  const chatMountRequested = useChatMountRequested();
  // 协议确认是否启用：按台子+按国家，来自 startup。startup 未到时为 undefined，
  // 此时不挂载；等它到了再挂。触发器那边会 await startup，不会漏事件。
  // 必须用 hook 订阅，不能用 popupConfig 里的 getXxxEnabled()：那两个是
  // useStartupStore.getState() 的一次性读取，App 不会因 startup 到达而重渲染。
  // 后果很实：启用协议确认的台子上，App 挂载时读到 undefined → 组件不挂载 →
  // 而 triggerAgreementConfirmation 里的 waitAgreementEnabled() 一拿到 startup
  // 就派发事件 —— 此时没有监听者，登录/充值点了没反应。
  // （getXxxEnabled() 留给 triggerAgreementConfirmation 这类模块级非 React 调用方。）
  const { data: startupDataForPopup, loading: startupLoadingForPopup } =
    useStartupData();
  // 三态折叠：字段缺失（老后端还没下发 / startup 失败）时按【启用】回退挂载，
  // 让组件自己打 config 接口定夺 —— 合规闸门只能往安全的一侧失效。
  // 判据集中在 resolvePrerequisiteFlag，与 getXxxEnabled()/waitAgreementEnabled() 同源。
  const startupSettledForPopup =
    !!startupDataForPopup ||
    startupLoadingForPopup === "success" ||
    startupLoadingForPopup === "error";
  const agreementEnabled = resolvePrerequisiteFlag(
    startupDataForPopup?.popupAgreementEnabled,
    startupSettledForPopup
  );

  // 获取在线客服配置（必须在条件 return 之前调用，保证 Hooks 数量一致，避免 React #300）
  const onlineServiceConfig = useMemo(() => {
    const onlineService = customerServices.find(
      service => service.customerCategory === "ONLINE_SERVICE"
    );
    if (!onlineService) return null;

    const serviceType = onlineService.serviceType;
    if (serviceType === "TAWK_TO") {
      // TAWK_TO 需要 propertyId 和 widgetId
      if (onlineService.propertyId && onlineService.widgetId) {
        return {
          type: "TAWK_TO" as const,
          propertyId: onlineService.propertyId,
          widgetId: onlineService.widgetId,
        };
      }
    } else if (serviceType === "LIVECHAT") {
      // LIVE_CHAT 需要 licenseId
      if (onlineService.licenseId) {
        return {
          type: "LIVE_CHAT" as const,
          licenseId: onlineService.licenseId,
        };
      }
    } else if (serviceType === "CHATWOOT") {
      // CHATWOOT 需要 baseUrl 和 websiteToken
      if (onlineService.baseUrl && onlineService.websiteToken) {
        return {
          type: "CHATWOOT" as const,
          baseUrl: onlineService.baseUrl,
          websiteToken: onlineService.websiteToken,
        };
      }
    }
    return null;
  }, [customerServices]);

  useEffect(() => {
    document.body.setAttribute("data-device", "mobile");

    // 真实 iOS 设备标记（电脑浏览器模拟器不会命中）
    const isRealIOS =
      ["iPad", "iPhone", "iPod"].some(d => navigator.platform?.includes(d)) ||
      (navigator.userAgent.includes("Mac") && "ontouchend" in document);
    if (isRealIOS) {
      document.body.classList.add("is-ios");
    }

    const isRealAndroid =
      /Android/i.test(navigator.userAgent) && "ontouchend" in document;
    if (isRealAndroid) {
      document.body.classList.add("is-android");
    }

    initScreenWidth();

    return () => {
      document.body.removeAttribute("data-device");
      document.body.classList.remove("is-ios");
      document.body.classList.remove("is-android");
    };
  }, []);

  // 设备被拉入黑名单时，仅展示拦截页，不展示当前页面信息（放在所有 Hooks 之后，避免 React #300）
  if (deviceBlacklisted) {
    return <BlockedDevicePage />;
  }

  // 应用主界面
  return (
    <div className="phone-wrapper">
      <AppRouter />
      {/* 客服 SDK：首屏完全不加载。openLiveChat / openTawkChat / openChatwoot 被调用时才挂载，
          省掉首屏 24 个请求 / 约 1MB / 5 次跨域握手（widget 本来就是 hidden 的）。 */}
      {chatMountRequested && (
        <Suspense fallback={null}>
          <SilentErrorBoundary>
            {onlineServiceConfig?.type === "TAWK_TO" && (
              <TawkToChat
                propertyId={onlineServiceConfig.propertyId}
                widgetId={onlineServiceConfig.widgetId}
                hideWidget={true}
                user={
                  isAuthenticated
                    ? {
                        userId: userId?.toString(),
                        name: username,
                        email: email,
                      }
                    : undefined
                }
              />
            )}
            {onlineServiceConfig?.type === "LIVE_CHAT" && (
              <LiveChat
                license={onlineServiceConfig.licenseId}
                hideWidget={true}
                user={
                  isAuthenticated
                    ? {
                        userId: userId?.toString(),
                        name: username,
                        email: email,
                      }
                    : undefined
                }
              />
            )}
            {onlineServiceConfig?.type === "CHATWOOT" && (
              <ChatwootChat
                baseUrl={onlineServiceConfig.baseUrl}
                websiteToken={onlineServiceConfig.websiteToken}
                hideWidget={true}
                user={
                  isAuthenticated
                    ? {
                        userId: userId?.toString(),
                        name: chatwootUserName,
                        email: email,
                      }
                    : undefined
                }
              />
            )}
          </SilentErrorBoundary>
        </Suspense>
      )}
      {/* 这两个原先和客服 SDK 挤在同一个块里，但与客服无关：走 deferredReady 延后挂载 */}
      {deferredReady && (
        <Suspense fallback={null}>
          <SilentErrorBoundary>
            <GlobalSafariAddHomeScreen />
            <GlobalRulesDialog />
          </SilentErrorBoundary>
        </Suspense>
      )}
      {/* AgreementConfirmation：事件模式，监听 show-agreement-confirmation。
          启用时不能挂在 deferredReady 后面 —— Login.handleLogin /
          Recharge.proceedDepositFlow 把整个后续流程都放在该事件的回调里，
          闸门最长要 8s 才放行，这段时间内点登录 = 事件没人接 = 按钮没反应。
          未启用时整块不渲染，lazy chunk 不 fetch、CSS 不进首屏（此时由
          triggerAgreementConfirmation 直接放行，不依赖监听器）。 */}
      {agreementEnabled && (
        <Suspense fallback={null}>
          <SilentErrorBoundary>
            <AgreementConfirmation />
          </SilentErrorBoundary>
        </Suspense>
      )}
      {/* 底部弹窗式充值 / 提现（lazy，见上方注释；事件驱动，故不进 deferredReady） */}
      <Suspense fallback={null}>
        <DepositPopup />
        <WithdrawPopup />
      </Suspense>
      {/* 保险信息弹窗（lazy，见上方注释；事件驱动，故不进 deferredReady） */}
      <Suspense fallback={null}>
        <InsurancePopup />
      </Suspense>
      {/* PopupQueueProvider + FloatingIconsProvider 包裹 LeftBottomFloatingIcons：
          useFloatingIcons 内部业务 hooks 依赖 PopupQueue 上下文，
          Context 缺失时 useFloatingIconsContext 走空 fallback，丢失首屏左下角图标（含客服）。
          LoginRegisterPopup 在此处以便使用 pause/resume 控制弹窗队列（首页自动触发时插队）。
          它已改 lazy，但必须独占一个 Suspense 边界：与下方那批 lazy 弹窗同边界的话，
          监听器要等整批 chunk 到齐才挂载，自动弹出会被拖很久（事件本身不会丢，
          triggerLoginRegisterPopup 的 listenerReady 闸门会缓存下来）。 */}
      {/* initialPaused 保留：本分支的注册弹窗闸门依赖它（PrerequisitePopups 与
          LoginRegisterPopup 两处 resume），去掉会让注册弹窗失去优先级保证。 */}
      {/* navigation 轴的额外导航件（supergol=左抽屉，监听 Header 的 open-left-drawer 事件）；
          不进弹窗队列、不依赖任何 Provider，template1 渲染 null */}
      <Suspense fallback={null}>
        <SilentErrorBoundary>
          <NavigationExtras />
        </SilentErrorBoundary>
      </Suspense>
      <PopupQueueProvider initialPaused>
        {/* 底部弹窗式登录注册，需在 PopupQueueProvider 内以访问 pause/resume。
            独占 Suspense：见上方说明。 */}
        <Suspense fallback={null}>
          <LoginRegisterPopup />
        </Suspense>
        {/* 放在 FloatingIconsProvider 之外：它只依赖 usePopupQueue，不读 floatingIcons，
            而 provider 已改 lazy —— 留在里面会让队列解锁跟着 chunk 一起被延后。 */}
        {/* PrerequisitePopups 必须【无条件挂载】：它是队列 initialPaused 的解锁点之一
              （另一个是 LoginRegisterPopup）。按开关做条件挂载会在开关为 false 时
              永远走不到 resume → 全站弹窗永久阻塞。年龄验证是否启用的判断已挪进
              组件内部（未启用直接跳过，不打 config 接口）。 */}
        <Suspense fallback={null}>
          <SilentErrorBoundary>
            <PrerequisitePopups />
          </SilentErrorBoundary>
        </Suspense>
        <Suspense fallback={null}>
          <FloatingIconsProvider>
            <SilentErrorBoundary>
              <LeftBottomFloatingIcons />
            </SilentErrorBoundary>
            {/* ActivityPopupBridge 是事件桥接（首页活动入口 → 打开活动弹窗），
                同样不能放进 deferredReady：闸门最长 8s，这段时间内点活动入口会没反应。
                必须在 FloatingIconsProvider 内：它要读 floatingIcons。 */}
            <SilentErrorBoundary>
              <ActivityPopupBridge />
            </SilentErrorBoundary>
            {/* 其余重弹窗延后到首屏资源落地（load + idle，8s 硬兜底）再下载挂载 */}
            {deferredReady && (
              <SilentErrorBoundary>
                <WebPush />
                <GlobalDownloadAppPopup />
                <GlobalAppDownloadDialog />
                <GlobalApkUpgradeDialog />
                <GlobalEvent
                  {...APP_GLOBAL_EVENT_FLOATING_VISIBILITY}
                  rechargeOfferVisible={false}
                />
              </SilentErrorBoundary>
            )}
          </FloatingIconsProvider>
        </Suspense>
      </PopupQueueProvider>
      {/* UnpaidGuideController：首次 mount 不会立即弹（只对挂载后新增的订单弹窗），
          首屏 1-3s 内不会有"新增订单"，可安全延迟 */}
      {deferredReady && (
        <Suspense fallback={null}>
          <UnpaidGuideController />
        </Suspense>
      )}
    </div>
  );
}

export default App;
